/**
 * Canonical registry of every customizable display element on customer-facing
 * screens. Global + per-company overrides in the database can flip visibility
 * or rename the label for any key here; anything not overridden uses the
 * defaults below. This file is the single source of truth for what an admin
 * can toggle on the Display Settings UI.
 *
 * Adding a new page: append entries; the admin UI auto-groups by `category`
 * and populates its page filter from the distinct categories present.
 */

export const DISPLAY_REGISTRY = [
  // ============================================
  // Agreement Detail — customer + admin agreement page
  // ============================================

  // Sections
  { key: 'agreementDetail.section.details', category: 'Agreement Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Agreement Details', defaultVisible: true },
  { key: 'agreementDetail.section.billingCoverage', category: 'Agreement Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Billing & Coverage', defaultVisible: true },
  { key: 'agreementDetail.section.items', category: 'Agreement Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Agreement Items', defaultVisible: true },
  { key: 'agreementDetail.section.total', category: 'Agreement Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Agreement Total', defaultVisible: true },

  // Agreement Details fields
  { key: 'agreementDetail.details.company', category: 'Agreement Detail', section: 'Details', kind: 'field',
    defaultLabel: 'Company', defaultVisible: true },
  { key: 'agreementDetail.details.type', category: 'Agreement Detail', section: 'Details', kind: 'field',
    defaultLabel: 'Agreement Type', defaultVisible: true },
  { key: 'agreementDetail.details.startDate', category: 'Agreement Detail', section: 'Details', kind: 'field',
    defaultLabel: 'Start Date', defaultVisible: true },
  { key: 'agreementDetail.details.endDate', category: 'Agreement Detail', section: 'Details', kind: 'field',
    defaultLabel: 'End Date', defaultVisible: true },
  { key: 'agreementDetail.details.billingCycle', category: 'Agreement Detail', section: 'Details', kind: 'field',
    defaultLabel: 'Billing Cycle', defaultVisible: true },
  { key: 'agreementDetail.details.workRole', category: 'Agreement Detail', section: 'Details', kind: 'field',
    defaultLabel: 'Work Role', defaultVisible: true },
  { key: 'agreementDetail.details.customerPO', category: 'Agreement Detail', section: 'Details', kind: 'field',
    defaultLabel: 'Customer PO', defaultVisible: true },

  // Billing & Coverage tiles
  { key: 'agreementDetail.billingCoverage.baseFee', category: 'Agreement Detail', section: 'Billing & Coverage', kind: 'field',
    defaultLabel: 'Base Recurring Fee', defaultVisible: true },
  { key: 'agreementDetail.billingCoverage.includedCoverage', category: 'Agreement Detail', section: 'Billing & Coverage', kind: 'field',
    defaultLabel: 'Included Coverage', defaultVisible: true },
  { key: 'agreementDetail.billingCoverage.overage', category: 'Agreement Detail', section: 'Billing & Coverage', kind: 'field',
    defaultLabel: 'Overage Behavior', defaultVisible: true },
  { key: 'agreementDetail.billingCoverage.overage.carryOver', category: 'Agreement Detail', section: 'Billing & Coverage', kind: 'field',
    defaultLabel: 'Carry over unused', defaultVisible: true },
  { key: 'agreementDetail.billingCoverage.overage.overruns', category: 'Agreement Detail', section: 'Billing & Coverage', kind: 'field',
    defaultLabel: 'Allow overruns', defaultVisible: true },
  { key: 'agreementDetail.billingCoverage.term', category: 'Agreement Detail', section: 'Billing & Coverage', kind: 'field',
    defaultLabel: 'Term', defaultVisible: true },
  { key: 'agreementDetail.billingCoverage.term.noEndDate', category: 'Agreement Detail', section: 'Billing & Coverage', kind: 'field',
    defaultLabel: 'No end date', defaultVisible: true },
  { key: 'agreementDetail.billingCoverage.term.oneTime', category: 'Agreement Detail', section: 'Billing & Coverage', kind: 'field',
    defaultLabel: 'One-time bill', defaultVisible: true },

  // Items table columns
  { key: 'agreementDetail.items.col.item', category: 'Agreement Detail', section: 'Items Table', kind: 'field',
    defaultLabel: 'Item', defaultVisible: true },
  { key: 'agreementDetail.items.col.qty', category: 'Agreement Detail', section: 'Items Table', kind: 'field',
    defaultLabel: 'Qty', defaultVisible: true },
  { key: 'agreementDetail.items.col.unitPrice', category: 'Agreement Detail', section: 'Items Table', kind: 'field',
    defaultLabel: 'Unit Price', defaultVisible: true },
  { key: 'agreementDetail.items.col.extended', category: 'Agreement Detail', section: 'Items Table', kind: 'field',
    defaultLabel: 'Extended', defaultVisible: true },

  // Total rows
  { key: 'agreementDetail.total.baseFee', category: 'Agreement Detail', section: 'Total', kind: 'field',
    defaultLabel: 'Base Recurring Fee', defaultVisible: true },
  { key: 'agreementDetail.total.additionsSubtotal', category: 'Agreement Detail', section: 'Total', kind: 'field',
    defaultLabel: 'Additions Subtotal', defaultVisible: true },
  { key: 'agreementDetail.total.grandTotal', category: 'Agreement Detail', section: 'Total', kind: 'field',
    defaultLabel: 'Total', defaultVisible: true },

  // ============================================
  // Invoice Detail — customer invoice page
  // ============================================

  // Sections
  { key: 'invoiceDetail.section.billingInfo', category: 'Invoice Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Billing Information', defaultVisible: true },
  { key: 'invoiceDetail.section.billableItems', category: 'Invoice Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Billable Items', defaultVisible: true },
  { key: 'invoiceDetail.section.breakdown', category: 'Invoice Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Invoice Breakdown', defaultVisible: true },

  // Summary tiles
  { key: 'invoiceDetail.summary.invoiceDate', category: 'Invoice Detail', section: 'Summary Tiles', kind: 'field',
    defaultLabel: 'Invoice Date', defaultVisible: true },
  { key: 'invoiceDetail.summary.dueDate', category: 'Invoice Detail', section: 'Summary Tiles', kind: 'field',
    defaultLabel: 'Due Date', defaultVisible: true },
  { key: 'invoiceDetail.summary.totalAmount', category: 'Invoice Detail', section: 'Summary Tiles', kind: 'field',
    defaultLabel: 'Total Amount', defaultVisible: true },
  { key: 'invoiceDetail.summary.balanceDue', category: 'Invoice Detail', section: 'Summary Tiles', kind: 'field',
    defaultLabel: 'Balance Due', defaultVisible: true },

  // Billing Information fields
  { key: 'invoiceDetail.billingInfo.billTo', category: 'Invoice Detail', section: 'Billing Information', kind: 'field',
    defaultLabel: 'Bill To', defaultVisible: true },
  { key: 'invoiceDetail.billingInfo.address', category: 'Invoice Detail', section: 'Billing Information', kind: 'field',
    defaultLabel: 'Billing Address', defaultVisible: true },
  { key: 'invoiceDetail.billingInfo.terms', category: 'Invoice Detail', section: 'Billing Information', kind: 'field',
    defaultLabel: 'Billing Terms', defaultVisible: true },
  { key: 'invoiceDetail.billingInfo.customerPO', category: 'Invoice Detail', section: 'Billing Information', kind: 'field',
    defaultLabel: 'Customer PO', defaultVisible: true },
  { key: 'invoiceDetail.billingInfo.reference', category: 'Invoice Detail', section: 'Billing Information', kind: 'field',
    defaultLabel: 'Reference', defaultVisible: true },
  { key: 'invoiceDetail.billingInfo.relatedTicket', category: 'Invoice Detail', section: 'Billing Information', kind: 'field',
    defaultLabel: 'Related Ticket', defaultVisible: true },

  // Time entries table columns
  { key: 'invoiceDetail.time.col.date', category: 'Invoice Detail', section: 'Time Entries Table', kind: 'field',
    defaultLabel: 'Date', defaultVisible: true },
  { key: 'invoiceDetail.time.col.member', category: 'Invoice Detail', section: 'Time Entries Table', kind: 'field',
    defaultLabel: 'Member', defaultVisible: true },
  { key: 'invoiceDetail.time.col.notes', category: 'Invoice Detail', section: 'Time Entries Table', kind: 'field',
    defaultLabel: 'Notes', defaultVisible: true },
  { key: 'invoiceDetail.time.col.hours', category: 'Invoice Detail', section: 'Time Entries Table', kind: 'field',
    defaultLabel: 'Hours', defaultVisible: true },
  { key: 'invoiceDetail.time.col.rate', category: 'Invoice Detail', section: 'Time Entries Table', kind: 'field',
    defaultLabel: 'Rate', defaultVisible: true },
  { key: 'invoiceDetail.time.col.amount', category: 'Invoice Detail', section: 'Time Entries Table', kind: 'field',
    defaultLabel: 'Amount', defaultVisible: true },

  // Products & other charges
  { key: 'invoiceDetail.products.header', category: 'Invoice Detail', section: 'Products Table', kind: 'field',
    defaultLabel: 'Products & Other Charges', defaultVisible: true },
  { key: 'invoiceDetail.products.col.description', category: 'Invoice Detail', section: 'Products Table', kind: 'field',
    defaultLabel: 'Description', defaultVisible: true },
  { key: 'invoiceDetail.products.col.qty', category: 'Invoice Detail', section: 'Products Table', kind: 'field',
    defaultLabel: 'Qty', defaultVisible: true },
  { key: 'invoiceDetail.products.col.rate', category: 'Invoice Detail', section: 'Products Table', kind: 'field',
    defaultLabel: 'Rate', defaultVisible: true },
  { key: 'invoiceDetail.products.col.amount', category: 'Invoice Detail', section: 'Products Table', kind: 'field',
    defaultLabel: 'Amount', defaultVisible: true },

  // Breakdown rows
  { key: 'invoiceDetail.breakdown.serviceTotal', category: 'Invoice Detail', section: 'Breakdown Rows', kind: 'field',
    defaultLabel: 'Service Total', defaultVisible: true },
  { key: 'invoiceDetail.breakdown.productTotal', category: 'Invoice Detail', section: 'Breakdown Rows', kind: 'field',
    defaultLabel: 'Product Total', defaultVisible: true },
  { key: 'invoiceDetail.breakdown.expenseTotal', category: 'Invoice Detail', section: 'Breakdown Rows', kind: 'field',
    defaultLabel: 'Expense Total', defaultVisible: true },
  { key: 'invoiceDetail.breakdown.subtotal', category: 'Invoice Detail', section: 'Breakdown Rows', kind: 'field',
    defaultLabel: 'Subtotal', defaultVisible: true },
  { key: 'invoiceDetail.breakdown.salesTax', category: 'Invoice Detail', section: 'Breakdown Rows', kind: 'field',
    defaultLabel: 'Sales Tax', defaultVisible: true },
  { key: 'invoiceDetail.breakdown.total', category: 'Invoice Detail', section: 'Breakdown Rows', kind: 'field',
    defaultLabel: 'Total', defaultVisible: true },
  { key: 'invoiceDetail.breakdown.paymentsApplied', category: 'Invoice Detail', section: 'Breakdown Rows', kind: 'field',
    defaultLabel: 'Payments Applied', defaultVisible: true },
  { key: 'invoiceDetail.breakdown.creditsApplied', category: 'Invoice Detail', section: 'Breakdown Rows', kind: 'field',
    defaultLabel: 'Credits Applied', defaultVisible: true },
  { key: 'invoiceDetail.breakdown.balanceDue', category: 'Invoice Detail', section: 'Breakdown Rows', kind: 'field',
    defaultLabel: 'Balance Due', defaultVisible: true },

  // ============================================
  // Ticket Detail — customer ticket page
  // ============================================

  // Sections
  { key: 'ticketDetail.section.description', category: 'Ticket Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Description', defaultVisible: true },
  { key: 'ticketDetail.section.attachments', category: 'Ticket Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Attachments', defaultVisible: true },
  { key: 'ticketDetail.section.conversation', category: 'Ticket Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Conversation', defaultVisible: true },

  // Metadata fields
  { key: 'ticketDetail.meta.created', category: 'Ticket Detail', section: 'Metadata', kind: 'field',
    defaultLabel: 'Created', defaultVisible: true },
  { key: 'ticketDetail.meta.priority', category: 'Ticket Detail', section: 'Metadata', kind: 'field',
    defaultLabel: 'Priority', defaultVisible: true },
  { key: 'ticketDetail.meta.board', category: 'Ticket Detail', section: 'Metadata', kind: 'field',
    defaultLabel: 'Board', defaultVisible: true },
  { key: 'ticketDetail.meta.assignedTo', category: 'Ticket Detail', section: 'Metadata', kind: 'field',
    defaultLabel: 'Assigned To', defaultVisible: true },

  // ============================================
  // Quote Detail — customer quote page
  // ============================================

  // Sections
  { key: 'quoteDetail.section.lineItems', category: 'Quote Detail', section: 'Sections', kind: 'section',
    defaultLabel: 'Line Items', defaultVisible: true },

  // Quote info fields
  { key: 'quoteDetail.info.company', category: 'Quote Detail', section: 'Quote Info', kind: 'field',
    defaultLabel: 'Company', defaultVisible: true },
  { key: 'quoteDetail.info.contact', category: 'Quote Detail', section: 'Quote Info', kind: 'field',
    defaultLabel: 'Contact', defaultVisible: true },
  { key: 'quoteDetail.info.paymentTerms', category: 'Quote Detail', section: 'Quote Info', kind: 'field',
    defaultLabel: 'Payment Terms', defaultVisible: true },
  { key: 'quoteDetail.info.shippingMethod', category: 'Quote Detail', section: 'Quote Info', kind: 'field',
    defaultLabel: 'Shipping Method', defaultVisible: true },
  { key: 'quoteDetail.info.expires', category: 'Quote Detail', section: 'Quote Info', kind: 'field',
    defaultLabel: 'Quote Expires', defaultVisible: true },

  // Line items table columns + totals
  { key: 'quoteDetail.lineItems.col.item', category: 'Quote Detail', section: 'Line Items Table', kind: 'field',
    defaultLabel: 'Item', defaultVisible: true },
  { key: 'quoteDetail.lineItems.col.qty', category: 'Quote Detail', section: 'Line Items Table', kind: 'field',
    defaultLabel: 'Qty', defaultVisible: true },
  { key: 'quoteDetail.lineItems.col.price', category: 'Quote Detail', section: 'Line Items Table', kind: 'field',
    defaultLabel: 'Price', defaultVisible: true },
  { key: 'quoteDetail.lineItems.col.total', category: 'Quote Detail', section: 'Line Items Table', kind: 'field',
    defaultLabel: 'Total', defaultVisible: true },
  { key: 'quoteDetail.lineItems.subtotal', category: 'Quote Detail', section: 'Line Items Table', kind: 'field',
    defaultLabel: 'Subtotal', defaultVisible: true },
  { key: 'quoteDetail.lineItems.salesTax', category: 'Quote Detail', section: 'Line Items Table', kind: 'field',
    defaultLabel: 'Sales Tax', defaultVisible: true },
  { key: 'quoteDetail.lineItems.total', category: 'Quote Detail', section: 'Line Items Table', kind: 'field',
    defaultLabel: 'Total', defaultVisible: true },
  { key: 'quoteDetail.lineItems.depositRequired', category: 'Quote Detail', section: 'Line Items Table', kind: 'field',
    defaultLabel: 'Deposit Required', defaultVisible: true },

  // ============================================
  // Dashboard — customer landing page
  // ============================================

  // Sections
  { key: 'dashboard.section.recentTickets', category: 'Dashboard', section: 'Sections', kind: 'section',
    defaultLabel: 'Recent Tickets', defaultVisible: true },
  { key: 'dashboard.section.openInvoices', category: 'Dashboard', section: 'Sections', kind: 'section',
    defaultLabel: 'Open Invoices', defaultVisible: true },
  { key: 'dashboard.section.quickActions', category: 'Dashboard', section: 'Sections', kind: 'section',
    defaultLabel: 'Quick Actions', defaultVisible: true },

  // Stat tiles
  { key: 'dashboard.stats.openTickets', category: 'Dashboard', section: 'Stat Tiles', kind: 'field',
    defaultLabel: 'Open Tickets', defaultVisible: true },
  { key: 'dashboard.stats.openInvoices', category: 'Dashboard', section: 'Stat Tiles', kind: 'field',
    defaultLabel: 'Open Invoices', defaultVisible: true },
  { key: 'dashboard.stats.amountDue', category: 'Dashboard', section: 'Stat Tiles', kind: 'field',
    defaultLabel: 'Amount Due', defaultVisible: true },
  { key: 'dashboard.stats.pendingQuotes', category: 'Dashboard', section: 'Stat Tiles', kind: 'field',
    defaultLabel: 'Pending Quotes', defaultVisible: true },

  // ============================================
  // List Pages — table/card headers on customer list views
  // ============================================

  // Invoices list columns
  { key: 'invoicesList.col.number', category: 'List Pages', section: 'Invoices List', kind: 'field',
    defaultLabel: 'Invoice #', defaultVisible: true },
  { key: 'invoicesList.col.date', category: 'List Pages', section: 'Invoices List', kind: 'field',
    defaultLabel: 'Date', defaultVisible: true },
  { key: 'invoicesList.col.dueDate', category: 'List Pages', section: 'Invoices List', kind: 'field',
    defaultLabel: 'Due Date', defaultVisible: true },
  { key: 'invoicesList.col.total', category: 'List Pages', section: 'Invoices List', kind: 'field',
    defaultLabel: 'Total', defaultVisible: true },
  { key: 'invoicesList.col.balance', category: 'List Pages', section: 'Invoices List', kind: 'field',
    defaultLabel: 'Balance', defaultVisible: true },
  { key: 'invoicesList.col.status', category: 'List Pages', section: 'Invoices List', kind: 'field',
    defaultLabel: 'Status', defaultVisible: true },

  // Agreements list card labels
  { key: 'agreementsList.row.startDate', category: 'List Pages', section: 'Agreements List', kind: 'field',
    defaultLabel: 'Start', defaultVisible: true },
  { key: 'agreementsList.row.type', category: 'List Pages', section: 'Agreements List', kind: 'field',
    defaultLabel: 'Type', defaultVisible: true },
];

// Fast lookup — most callers only need "what's the default for this key".
const REGISTRY_BY_KEY = Object.fromEntries(
  DISPLAY_REGISTRY.map(entry => [entry.key, entry])
);

export const REGISTRY_CATEGORIES = Array.from(
  new Set(DISPLAY_REGISTRY.map(e => e.category))
);

/**
 * Resolve visible/label for a key, given the effective overrides map from the
 * backend. Overrides win when set to a non-null value; otherwise the registry
 * default applies. Unknown keys return the safe defaults { visible: true,
 * label: '' } so pages don't crash on an out-of-sync registry.
 */
export function resolveDisplay(key, effectiveOverrides) {
  const entry = REGISTRY_BY_KEY[key];
  const override = effectiveOverrides?.[key] || {};
  return {
    visible: override.visible ?? entry?.defaultVisible ?? true,
    label: override.label ?? entry?.defaultLabel ?? '',
  };
}
